/**
 * /playbook · M03 方案管理（S2 Day 7b · T11 §3.2 路由 → M03）
 *
 * 四 tab：PB 列表 / PB 编辑 / RoutingTable / 兜底映射
 */
import { createFileRoute, useRouterState } from "@tanstack/react-router";

import { M03Shell, type M03View } from "@/components/m03/m03-shell";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";

export const Route = createFileRoute("/playbook")({
	component: PlaybookPage,
});

function PlaybookPage() {
	const view = getPlaybookView(useRouteHash());
	const copy = PLAYBOOK_VIEW_COPY[view];

	return (
		<div className="space-y-4">
			<PageHeader
				moduleId="M03"
				title={copy.title}
				description={copy.description}
				help={copy.help}
			/>
			<M03Shell view={view} />
		</div>
	);
}

function useRouteHash() {
	const routeHash = useRouterState({ select: (state) => state.location.hash });
	if (routeHash) return routeHash;
	if (typeof window === "undefined") return "";
	return window.location.hash;
}

function getPlaybookView(hash: string): M03View {
	const normalized = hash.replace(/^#/, "");
	return normalized === "playbook-test" ? "test" : "management";
}

const playbookHelpSections: PageHelp["sections"] = [
	{
		title: "这个页面用来做什么",
		items: [
			"查看每类问题当前对应的处理方案。",
			"维护方案步骤、允许调用的工具、自动预取事实和失败兜底。",
			"配置变更后回到方案测试和评测运营页面跑样本，确认没有影响主链路。",
		],
	},
	{
		title: "字段怎么看",
		items: [
			"allowed_skills 表示该方案允许使用的工具与动作。",
			"step_budget 表示最多允许走多少个处理步骤，防止链路失控。",
			"pre_fetch_spec 表示生成回复前需要预先查询哪些事实。",
			"RoutingTable 表示意图命中后的主路由；兜底映射只在 RoutingTable 未命中时使用。",
		],
	},
	{
		title: "通过标准",
		items: [
			"方案能说明适用问题、处理目标、需要客户补充的信息和自动查询的数据。",
			"修改方案后，评测运营没有出现新增失败样本。",
			"方案测试只看本环节召回，不替代工具事实和最终回复验证。",
		],
	},
];

const PLAYBOOK_VIEW_COPY: Record<
	M03View,
	{
		title: string;
		description: string;
		help: PageHelp;
	}
> = {
	management: {
		title: "方案管理",
		description: "维护处理方案、RoutingTable 主路由和兜底映射。",
		help: {
			title: "方案管理说明",
			description: "这里管理客户问题进入客服链路后应该按哪套流程处理。",
			sections: playbookHelpSections,
		},
	},
	test: {
		title: "方案测试",
		description: "用模拟问题验证方案命中、兜底和后续评测承接。",
		help: {
			title: "方案测试说明",
			description: "这里用于单独验证处理方案是否命中正确，并把结果带入评测运营继续闭环。",
			sections: playbookHelpSections,
		},
	},
};
